import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Download, IndianRupee, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { formatDate } from "../../Utils/dateFormatter";
import useSettlement from "../../../Hooks/useSettlement";
import { exportAnyJsonToExcel } from "../../Utils/exportAnyJsonToExcel";
import TablePagination from "../../Utils/TablePagination";

/* ---------------- DATE HELPERS ---------------- */

const getInitialDateRange = () => ({
  fromDate: "",
  toDate: "",
});

/* ---------------- FORMAT HELPERS ---------------- */

const formatAmount = (amount) => {
  return Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCreatedOn = (value) => {
  if (!value) return "-";

  // Period format: YYYY-MM-DD:YYYY-MM-DD
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [fromDate, toDate] = value.split(":");

    return `${formatDate(fromDate)} to ${formatDate(toDate)}`;
  }

  return formatDate(value);
};

/* ---------------- COMPONENT ---------------- */

export default function VolunteerSettlement() {
  const {
    volunteerSettlements,
    volunteerSettlementDetails,
    volunteerSettlementLoading,
    fetchVolunteerSettlementAmount,
  } = useSettlement();

  const initialDates = useMemo(() => getInitialDateRange(), []);

  const [dateFilter, setDateFilter] = useState(initialDates);
  const [settlementPage, setSettlementPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchingSettlement, setFetchingSettlement] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const pageSize = 25;

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    setSettlementPage(1);
  }, [volunteerSettlements]);

  /* ---------------- CALCULATIONS ---------------- */

  const totalSettlementAmount = useMemo(() => {
    return (volunteerSettlements || []).reduce(
      (total, item) => total + Number(item?.total_amount || 0),
      0,
    );
  }, [volunteerSettlements]);

  const paidSettlementCount = useMemo(() => {
    return (volunteerSettlements || []).filter(
      (item) =>
        String(item?.status || "")
          .trim()
          .toUpperCase() === "Y",
    ).length;
  }, [volunteerSettlements]);

  const pendingSettlementCount = useMemo(() => {
    return (volunteerSettlements || []).filter(
      (item) =>
        String(item?.status || "")
          .trim()
          .toUpperCase() !== "Y",
    ).length;
  }, [volunteerSettlements]);

  const paginatedSettlements = useMemo(() => {
    const startIndex = (settlementPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return (volunteerSettlements || []).slice(startIndex, endIndex);
  }, [volunteerSettlements, settlementPage]);

  /* ---------------- VALIDATION ---------------- */

  const validateDates = () => {
    if (!dateFilter.fromDate || !dateFilter.toDate) {
      toast.error("Please select from date and to date");
      return false;
    }

    if (dateFilter.fromDate > dateFilter.toDate) {
      toast.error("From date cannot be greater than to date");
      return false;
    }

    return true;
  };

  /* ---------------- HANDLERS ---------------- */

  const handleDateChange = (event) => {
    const { name, value } = event.target;

    setDateFilter((previous) => ({
      ...previous,
      [name]: value,
    }));

    setSettlementPage(1);
  };

  const handleFetchSettlement = async () => {
    if (!validateDates()) return;

    try {
      setFetchingSettlement(true);

      const result = await fetchVolunteerSettlementAmount({
        fromDate: dateFilter.fromDate,
        toDate: dateFilter.toDate,
      });

      setHasFetched(true);

      if (Array.isArray(result) && result.length > 0) {
        toast.success("Settlement data fetched successfully");
      }
    } catch (error) {
      console.error("handleFetchSettlement error:", error);
    } finally {
      setFetchingSettlement(false);
    }
  };

  const handleRefresh = async () => {
    if (!validateDates()) return;

    try {
      setRefreshing(true);

      const result = await fetchVolunteerSettlementAmount(
        {
          fromDate: dateFilter.fromDate,
          toDate: dateFilter.toDate,
        },
        true,
      );

      setHasFetched(true);
      setSettlementPage(1);

      if (Array.isArray(result)) {
        toast.success("Settlement data refreshed successfully");
      }
    } catch (error) {
      console.error("handleRefresh error:", error);
      toast.error("Unable to refresh settlement data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleExportSettlement = () => {
    if (!volunteerSettlements?.length) {
      toast.error("No settlement data available to export");
      return;
    }

    const exportData = volunteerSettlements.map((item, index) => ({
      "S/N": index + 1,
      "Volunteer Code": item.volunteer_code || "-",
      "Volunteer ID": item.volunteer_id || "-",
      "Total Amount": Number(item.total_amount || 0),
      "Bank Name": item.bank_name || "-",
      "Account Holder": item.bank_holder_name || "-",
      "Account Number": item.account_no || "-",
      "IFSC Code": item.ifsc_code || "-",
      Status:
        String(item.status || "")
          .trim()
          .toUpperCase() === "Y"
          ? "Settled"
          : "Pending",
      Period: formatCreatedOn(item.created_on),
    }));

    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `VolunteerSettlement_${dateFilter.fromDate}_${dateFilter.toDate}`,
      sheetName: "Volunteer Settlement",
    });
  };

  const isButtonLoading =
    refreshing || fetchingSettlement || volunteerSettlementLoading;

 return (
   <div className="h-full flex flex-col overflow-hidden">
     <div className="shrink-0 space-y-5">
       {/* HEADER */}

       <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
         <div>
           <h2 className="text-lg font-bold text-slate-900">
             Volunteer Settlement
           </h2>

           <p className="mt-1 text-xs text-slate-500">
             View volunteer settlement amounts and bank details using a custom
             date range.
           </p>
         </div>

         <button
           type="button"
           onClick={handleRefresh}
           disabled={
             isButtonLoading || !dateFilter.fromDate || !dateFilter.toDate
           }
           className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-500 px-4 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
         >
           <RefreshCcw size={15} className={refreshing ? "animate-spin" : ""} />

           {refreshing ? "Refreshing..." : "Refresh"}
         </button>
       </div>

       {/* SETTLEMENT FILTER */}

       <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
         <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50 via-white to-blue-50 px-5 py-4">
           <div className="flex items-center gap-3">
             <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
               <CalendarDays size={19} />
             </div>

             <div>
               <h3 className="text-base font-semibold text-slate-900">
                 Settlement Amount Report
               </h3>

               <p className="text-xs text-slate-500">
                 Select from date and to date according to your requirement.
               </p>
             </div>
           </div>
         </div>

         <div className="p-5">
           <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[1fr_1fr_auto_auto] xl:items-end">
             <div>
               <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                 From Date
               </label>

               <input
                 type="date"
                 name="fromDate"
                 value={dateFilter.fromDate}
                 max={dateFilter.toDate || undefined}
                 onChange={handleDateChange}
                 className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-50"
               />
             </div>

             <div>
               <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                 To Date
               </label>

               <input
                 type="date"
                 name="toDate"
                 value={dateFilter.toDate}
                 min={dateFilter.fromDate || undefined}
                 onChange={handleDateChange}
                 className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-50"
               />
             </div>

             <button
               type="button"
               onClick={handleFetchSettlement}
               disabled={
                 isButtonLoading || !dateFilter.fromDate || !dateFilter.toDate
               }
               className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
             >
               <RefreshCcw
                 size={15}
                 className={fetchingSettlement ? "animate-spin" : ""}
               />

               {fetchingSettlement ? "Fetching..." : "Get Settlement"}
             </button>

             <button
               type="button"
               onClick={handleExportSettlement}
               disabled={!volunteerSettlements?.length || isButtonLoading}
               className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
             >
               <Download size={15} />
               Export
             </button>
           </div>
         </div>
       </div>

       {/* SUMMARY CARDS */}

       <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
         <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
           <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
             Report Type
           </p>

           <p className="mt-2 text-lg font-bold text-blue-900">
             {volunteerSettlementDetails?.type || "Settlement Amount"}
           </p>
         </div>

         <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
           <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">
             Total Volunteers
           </p>

           <p className="mt-2 text-2xl font-bold text-violet-900">
             {volunteerSettlements?.length || 0}
           </p>
         </div>

         <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
           <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
             Total Amount
           </p>

           <p className="mt-2 text-2xl font-bold text-emerald-900">
             ₹{formatAmount(totalSettlementAmount)}
           </p>
         </div>

         <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
           <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
             Pending / Settled
           </p>

           <p className="mt-2 text-2xl font-bold text-amber-900">
             {pendingSettlementCount} / {paidSettlementCount}
           </p>
         </div>
       </div>
     </div>

     {/* SETTLEMENT TABLE */}

     <div className="flex-1 min-h-0 mt-5 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
       <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
         <div>
           <h3 className="text-base font-semibold text-slate-900">
             Settlement Details
           </h3>

           <p className="text-xs text-slate-500">
             {dateFilter.fromDate || "-"} to {dateFilter.toDate || "-"}
           </p>
         </div>

         <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
           ₹{formatAmount(totalSettlementAmount)}
         </div>
       </div>

       <div className="flex-1 min-h-0 overflow-auto">
         <table className="w-full min-w-[1200px] bg-white">
           <thead className="sticky top-0 z-20">
             <tr className="bg-blue-400 text-xs font-bold uppercase tracking-wide text-white">
               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 S/N
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Volunteer Code
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Amount
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Account Holder
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Bank
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Account Number
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 IFSC
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Period
               </th>

               <th className="bg-blue-400 whitespace-nowrap px-4 py-3 text-center">
                 Status
               </th>
             </tr>
           </thead>

           <tbody className="divide-y divide-slate-100">
             {paginatedSettlements.length > 0 ? (
               paginatedSettlements.map((item, index) => {
                 const isSettled =
                   String(item.status || "")
                     .trim()
                     .toUpperCase() === "Y";

                 return (
                   <tr
                     key={
                       item.volunteer_id || `${item.volunteer_code}-${index}`
                     }
                     className="text-sm transition hover:bg-emerald-50/40"
                   >
                     <td className="px-4 py-4 text-center text-slate-600">
                       {(settlementPage - 1) * pageSize + index + 1}
                     </td>

                     <td className="px-4 py-4 text-center">
                       <span className="whitespace-nowrap rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                         {item.volunteer_code || "-"}
                       </span>
                     </td>

                     <td className="px-4 py-4 text-center">
                       <span className="inline-flex items-center gap-1 whitespace-nowrap font-bold text-emerald-700">
                         <IndianRupee size={14} />
                         {formatAmount(item.total_amount)}
                       </span>
                     </td>

                     <td className="whitespace-nowrap px-4 py-4 text-center font-medium text-slate-800">
                       {item.bank_holder_name || "-"}
                     </td>

                     <td className="whitespace-nowrap px-4 py-4 text-center text-slate-600">
                       {item.bank_name || "-"}
                     </td>

                     <td className="whitespace-nowrap px-4 py-4 text-center font-mono text-xs text-slate-600">
                       {item.account_no || "-"}
                     </td>

                     <td className="whitespace-nowrap px-4 py-4 text-center font-mono text-xs font-semibold text-slate-700">
                       {item.ifsc_code || "-"}
                     </td>

                     <td className="whitespace-nowrap px-4 py-4 text-center text-xs text-slate-500">
                       {formatCreatedOn(item.created_on)}
                     </td>

                     <td className="px-4 py-4 text-center">
                       <span
                         className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                           isSettled
                             ? "bg-emerald-100 text-emerald-700"
                             : "bg-amber-100 text-amber-700"
                         }`}
                       >
                         {isSettled ? "Settled" : "Pending"}
                       </span>
                     </td>
                   </tr>
                 );
               })
             ) : (
               <tr>
                 <td
                   colSpan={9}
                   className="px-5 py-14 text-center text-sm text-slate-500"
                 >
                   {hasFetched
                     ? "No settlement data found for selected date range"
                     : "Select from date and to date, then click Get Settlement"}
                 </td>
               </tr>
             )}
           </tbody>
         </table>
       </div>

       {volunteerSettlements?.length > pageSize && (
         <div className="shrink-0 border-t border-slate-100 px-4 py-3">
           <TablePagination
             currentPage={settlementPage}
             totalCount={volunteerSettlements.length}
             pageSize={pageSize}
             onPageChange={setSettlementPage}
           />
         </div>
       )}
     </div>
   </div>
 );
}
